import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with credentials
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach Authorization header from localStorage as a fallback (cross-origin cookies edge cases)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("emad_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// -----------------------------------------------------------------------------
// Idempotency-Key auto-injection for write endpoints that require it.
// Server-side is the authoritative dedup mechanism (unique index +
// atomic acquire). Client-side cache exists ONLY to make double-click
// return the same resource_id — it is NOT a substitute for server protection.
// -----------------------------------------------------------------------------
const IDEMPOTENT_ROUTES = [
  /^\/?opportunities\/?(\?|$)/,
  /^\/?complaints\/?(\?|$)/,
  /^\/?courses\/?(\?|$)/,
  /^\/?recruiter\/contact-request\/?(\?|$)/,
  /^\/?periodic-reports\/?(\?|$)/,
  // Wave 3B — funding capability requires Idempotency-Key on POST and PATCH.
  /^\/?funding\//,
];

function _canonicalBodyHash(body) {
  try {
    if (body == null) return "0";
    const raw = typeof body === "string" ? body : JSON.stringify(body, Object.keys(body || {}).sort());
    let h = 5381;
    for (let i = 0; i < raw.length; i++) h = ((h << 5) + h + raw.charCodeAt(i)) | 0;
    return String(h >>> 0);
  } catch { return String(Math.random()); }
}

const _idemCache = new Map(); // `${route}:${bodyHash}` -> { key, expiresAt }
const _IDEM_TTL_MS = 5000;

function _newUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // RFC-ish fallback
  return "idem-" + Date.now() + "-" + Math.random().toString(36).slice(2, 14);
}

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (method !== "POST" && method !== "PATCH") return config;
  const url = (config.url || "").split("?")[0];
  if (!IDEMPOTENT_ROUTES.some((re) => re.test(url))) return config;
  config.headers = config.headers || {};
  if (config.headers["Idempotency-Key"]) return config;
  const cacheKey = `${method}:${url}:${_canonicalBodyHash(config.data)}`;
  const now = Date.now();
  let entry = _idemCache.get(cacheKey);
  if (!entry || entry.expiresAt < now) {
    entry = { key: _newUuid(), expiresAt: now + _IDEM_TTL_MS };
    _idemCache.set(cacheKey, entry);
  }
  config.headers["Idempotency-Key"] = entry.key;
  return config;
});

// Successful POSTs to idempotent routes clear their cache entry so the next
// legitimate submission gets a fresh key (natural client-side reset).
api.interceptors.response.use((response) => {
  try {
    const method = (response.config?.method || "").toUpperCase();
    if ((method === "POST" || method === "PATCH") && response.status >= 200 && response.status < 300) {
      const url = (response.config?.url || "").split("?")[0];
      if (IDEMPOTENT_ROUTES.some((re) => re.test(url))) {
        const cacheKey = `${method}:${url}:${_canonicalBodyHash(response.config?.data ? JSON.parse(response.config.data) : null)}`;
        _idemCache.delete(cacheKey);
      }
    }
  } catch { /* swallow */ }
  return response;
});

const API_ERROR_CODE_MESSAGES = {
  CURRENT_ASSOCIATION_AGREEMENT_EXISTS:
    "توجد اتفاقية سارية أو قيد التوقيع لهذه الجمعية. لا يمكن إصدار اتفاقية جديدة قبل انتهاء أو إلغاء الحالية.",
  ACTIVE_ASSOCIATION_AGREEMENT_REQUIRED:
    "يتطلب إتمام هذه العملية وجود اتفاقية جمعية سارية تغطي فترة التدريب.",
  ACTIVE_ASSOCIATION_AGREEMENTS_REQUIRED:
    "لا يمكن نشر الفرصة قبل توقيع اتفاقية سارية تغطي فترة التدريب مع الجمعية المُقدِّمة. اذهب إلى «العقود» ← «اتفاقيات الجمعيات» وأصدر الاتفاقية أولاً.",
  TRAINING_PERIOD_REQUIRED_FOR_PUBLICATION:
    "لا يمكن اعتماد الفرصة قبل تحديد تاريخ بداية التدريب وعدد الأسابيع (تُحسب النهاية تلقائياً).",
  INVALID_TRAINING_PERIOD:
    "تاريخ نهاية التدريب يجب أن يكون بعد بدايته.",
  OPPORTUNITY_ASSOCIATIONS_INVALID:
    "توجد جمعيات غير صالحة أو غير مفعّلة ضمن مواقع تنفيذ الفرصة.",
  PUBLISHED_CONTRACT_TEMPLATE_REQUIRED:
    "لا يوجد قالب عقد منشور. يرجى نشر القالب أولاً من صفحة التكاملات.",
};

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "حدث خطأ غير متوقع";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" - ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.code === "string") {
    let base = API_ERROR_CODE_MESSAGES[detail.code] || detail.code;
    // Enrich with association names when the server returns them
    const missing = Array.isArray(detail.missing_associations) ? detail.missing_associations : [];
    if (missing.length > 0) {
      const names = missing.map((m) => m.association_name).filter(Boolean).join("، ");
      if (names) base += `\nالجمعيات: ${names}`;
    }
    const invalid = Array.isArray(detail.associations) ? detail.associations : [];
    if (invalid.length > 0) {
      base += `\nعدد الجمعيات غير الصالحة: ${invalid.length}`;
    }
    return base;
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return "حدث خطأ غير متوقع";
  }
}

export const ROLE_LABELS = {
  super_admin: "مدير النظام",
  student: "طالب متدرب",
  association: "جمعية",
  mentor: "موجّه",
  recreation_mentor: "موجّه إثرائي",
  content_creator: "معدّ محتوى",
  donor: "مؤسسة مانحة",
  recruiter: "جهة استقطاب",
  faculty: "عضو هيئة تدريس جامعي",
};

export const APP_STATUS_LABELS = {
  pending: "قيد الانتظار",
  under_review: "قيد المراجعة",
  accepted: "مقبول",
  rejected: "مرفوض",
  assigned: "تم التسكين داخل منظمة",
};

export const OPP_STATUS_LABELS = {
  draft: "مسودة",
  pending_contract: "بانتظار اتفاقية التعاون",
  under_review: "تحت المراجعة",
  needs_edit: "يحتاج تعديل",
  published: "منشورة",
  closed: "مغلقة",
  archived: "مؤرشفة",
  rejected: "مرفوضة",
};
